import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const residualConnectionComponent: ComponentDef = {
  id: 'residual-connection',
  name: 'Residual Connection',
  description: 'Skip connection patterns: Pre-Norm, Post-Norm, DeepNorm, Sandwich, and Parallel (PaLM).',
  category: 'component',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    { name: 'd_ff', label: 'Sub-layer FFN dim', type: 'number', default: 2048, min: 64, max: 16384, step: 64 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'pre_norm',
      options: [
        { label: 'Pre-Norm', value: 'pre_norm' },
        { label: 'Post-Norm', value: 'post_norm' },
        { label: 'DeepNorm', value: 'deepnorm' },
        { label: 'Sandwich', value: 'sandwich' },
        { label: 'Parallel (PaLM)', value: 'parallel' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const d_ff = Number(params.d_ff);
    const variant = String(params.variant || 'pre_norm');

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);

    if (variant === 'post_norm') {
      // Post-Norm (original Transformer): x + Norm(Sublayer(x))
      g.linear('up', 'x', [d_model, d_ff]);
      g.activation('act', 'up', 'GELU');
      g.linear('down', 'act', [d_ff, d_model]);
      g.add('residual', 'x', 'down');
      g.norm('out', 'residual', 'LayerNorm');
      g.output('out', 'out');

    } else if (variant === 'deepnorm') {
      // DeepNorm: x * alpha + Norm(Sublayer(x)), alpha = (2N)^(1/4) for N layers
      g.scale('scaled_x', 'x', 'alpha=(2N)^{1/4}');
      g.linear('up', 'x', [d_model, d_ff]);
      g.activation('act', 'up', 'GELU');
      g.linear('down', 'act', [d_ff, d_model]);
      g.add('residual', 'scaled_x', 'down');
      g.norm('out', 'residual', 'DeepNorm');
      g.output('out', 'out');

    } else if (variant === 'sandwich') {
      // Sandwich: Norm(x + Norm(Sublayer(x)))  -- extra norm wrapping
      g.norm('inner_norm_x', 'x', 'LayerNorm');
      g.linear('up', 'inner_norm_x', [d_model, d_ff]);
      g.activation('act', 'up', 'GELU');
      g.linear('down', 'act', [d_ff, d_model]);
      g.norm('inner_norm_sub', 'down', 'LayerNorm');
      g.add('residual', 'x', 'inner_norm_sub');
      g.norm('out', 'residual', 'LayerNorm');
      g.output('out', 'out');

    } else if (variant === 'parallel') {
      // Parallel (PaLM): attention and FFN run in parallel, both norm-first, then summed
      // Norm(x) -> Attn branch and FFN branch in parallel, both added to x
      g.norm('norm_x', 'x', 'LayerNorm');

      // Attention branch (simplified as linear)
      g.linear('attn_out', 'norm_x', [d_model, d_model]);

      // FFN branch
      g.linear('ffn_up', 'norm_x', [d_model, d_ff]);
      g.activation('ffn_act', 'ffn_up', 'GELU');
      g.linear('ffn_down', 'ffn_act', [d_ff, d_model]);

      // Both branches added to residual simultaneously
      g.add('partial', 'x', 'attn_out');
      g.add('out', 'partial', 'ffn_down');
      g.output('out', 'out');

    } else {
      // Pre-Norm (default, GPT-2 / LLaMA style): x + Sublayer(Norm(x))
      g.norm('norm_x', 'x', 'LayerNorm');
      g.linear('up', 'norm_x', [d_model, d_ff]);
      g.activation('act', 'up', 'GELU');
      g.linear('down', 'act', [d_ff, d_model]);
      g.add('out', 'x', 'down');
      g.output('out', 'out');
    }

    return g.build();
  }
};
